export type Provider = "google" | "microsoft" | "apple";

interface ProviderConfig {
  authorizeUrl: string;
  clientId: string;
  scope: string;
  extraParams?: Record<string, string>;
}

/** Lê uma var do Vite, tratando string vazia como ausente. */
function envVar(key: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[key];
  return value && value.length > 0 ? value : undefined;
}

function providerConfig(provider: Provider): ProviderConfig | null {
  switch (provider) {
    case "google": {
      const clientId = envVar("VITE_GOOGLE_CLIENT_ID");
      if (!clientId) return null;
      return {
        authorizeUrl: "https://accounts.google.com/o/oauth2/v2/auth",
        clientId,
        scope: "openid email profile",
        extraParams: { prompt: "select_account", access_type: "online" },
      };
    }
    case "microsoft": {
      const clientId = envVar("VITE_MICROSOFT_CLIENT_ID");
      if (!clientId) return null;
      const authority = envVar("VITE_MICROSOFT_AUTHORITY") ?? "https://login.microsoftonline.com/common";
      return {
        authorizeUrl: `${authority}/oauth2/v2.0/authorize`,
        clientId,
        scope: "openid email profile",
        extraParams: { prompt: "select_account" },
      };
    }
    case "apple": {
      // ⚠️ Apple exige response_mode=form_post quando se pede o escopo name/email,
      // o que faz o navegador dar POST no redirect_uri — uma SPA não lê esse POST.
      // Logo o fluxo Apple precisa de um handler de redirect no backend; esta rota
      // de callback (query string) cobre só Google e Microsoft. Ver imobgo-hub#207.
      const clientId = envVar("VITE_APPLE_CLIENT_ID");
      if (!clientId) return null;
      return {
        authorizeUrl: "https://appleid.apple.com/auth/authorize",
        clientId,
        scope: "name email",
        extraParams: { response_mode: "form_post" },
      };
    }
  }
}

/** True quando há client_id configurado para o provider (senão cai no mock). */
export function isProviderConfigured(provider: Provider): boolean {
  return providerConfig(provider) !== null;
}

/**
 * Redirect URI por ambiente: deriva do origin atual, então casa automaticamente
 * com dev / hml / prod. Tem que bater exatamente com o que está registrado no
 * provider (sem barra final).
 */
export function redirectUriFor(provider: Provider): string {
  return `${window.location.origin}/auth/callback/${provider}`;
}

const STORAGE_KEY = "imobgo.agency.oauth";

interface PendingOAuth {
  provider: Provider;
  state: string;
  codeVerifier: string;
}

export class OAuthStateError extends Error {
  constructor(public readonly reason: "missing" | "mismatch") {
    super(`oauth state ${reason}`);
    this.name = "OAuthStateError";
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomToken(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function challengeFromVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Inicia o Authorization Code Flow + PKCE: guarda verifier/state em sessionStorage
 * e redireciona o navegador para a tela de consentimento do provider.
 */
export async function startOAuthRedirect(provider: Provider): Promise<void> {
  const config = providerConfig(provider);
  if (!config) throw new Error(`Provider "${provider}" não configurado.`);

  const state = randomToken(24);
  const codeVerifier = randomToken(48);
  const codeChallenge = await challengeFromVerifier(codeVerifier);

  const pending: PendingOAuth = { provider, state, codeVerifier };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUriFor(provider),
    response_type: "code",
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    ...config.extraParams,
  });

  window.location.assign(`${config.authorizeUrl}?${params.toString()}`);
}

/**
 * Recupera o PKCE pendente e valida o `state` retornado (anti-CSRF).
 * Consome (remove) o registro independentemente do resultado.
 */
export function consumePendingOAuth(
  provider: Provider,
  returnedState: string | null,
): PendingOAuth {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);

  if (!raw) throw new OAuthStateError("missing");

  const pending = JSON.parse(raw) as PendingOAuth;
  if (pending.provider !== provider || !returnedState || pending.state !== returnedState) {
    throw new OAuthStateError("mismatch");
  }
  return pending;
}
